import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Show, SignIn, SignUp } from "@clerk/react";
import { Toaster } from "sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { MeProvider, useMe } from "@/context/MeContext";
import { CartProvider } from "@/context/CartContext";
import AppShell from "@/components/AppShell";
import { Spinner } from "@/components/common";
import Landing from "@/pages/Landing";
import AuthLayout from "@/pages/AuthLayout";
import Onboarding from "@/pages/Onboarding";
import OwnerHome from "@/pages/owner/OwnerHome";
import OwnerMaintenance from "@/pages/owner/OwnerMaintenance";
import Store from "@/pages/owner/Store";
import OwnerOrders from "@/pages/owner/OwnerOrders";
import EmployeeTasks from "@/pages/EmployeeTasks";
import AdminDashboard from "@/pages/admin/AdminDashboard";

const homeFor = (me) => {
  if (!me) return "/";
  if (me.role === "admin") return "/admin";
  if (me.role === "employee") return "/employee";
  if (me.status === "approved" && me.project?.active) return "/owner";
  return "/onboarding";
};

const Guard = ({ allow, children }) => {
  const { me, loading, error } = useMe();
  if (loading) return <Spinner />;
  if (error || !me) return <p className="p-10 text-center" data-testid="me-error">تعذر تحميل بيانات الحساب، حدّث الصفحة.</p>;
  if (!allow(me)) return <Navigate to={homeFor(me)} replace />;
  return children;
};

const SignedInOnly = ({ children }) => (
  <>
    <Show when="signed-in">{children}</Show>
    <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
  </>
);

const Home = () => {
  const { me, loading } = useMe();
  return (
    <>
      <Show when="signed-out"><Landing /></Show>
      <Show when="signed-in">{loading ? <Spinner /> : <Navigate to={homeFor(me)} replace />}</Show>
    </>
  );
};

const isOwner = (me) => me.role === "customer" && me.status === "approved" && me.project?.active;

function App() {
  return (
    <div className="App" dir="rtl">
      <BrowserRouter>
        <ClerkAuthProvider>
          <MeProvider>
            <CartProvider>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/sign-in/*" element={<AuthLayout><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthLayout>} />
                <Route path="/sign-up/*" element={<AuthLayout><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthLayout>} />
                <Route path="/onboarding" element={<SignedInOnly><Guard allow={(m) => m.role === "customer" && !isOwner(m)}><Onboarding /></Guard></SignedInOnly>} />
                <Route element={<SignedInOnly><AppShell /></SignedInOnly>}>
                  <Route path="/owner" element={<Guard allow={isOwner}><OwnerHome /></Guard>} />
                  <Route path="/owner/maintenance" element={<Guard allow={isOwner}><OwnerMaintenance /></Guard>} />
                  <Route path="/owner/store" element={<Guard allow={isOwner}><Store /></Guard>} />
                  <Route path="/owner/orders" element={<Guard allow={isOwner}><OwnerOrders /></Guard>} />
                  <Route path="/employee" element={<Guard allow={(m) => m.role === "employee"}><EmployeeTasks /></Guard>} />
                  <Route path="/admin" element={<Guard allow={(m) => m.role === "admin"}><AdminDashboard /></Guard>} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Toaster position="top-center" richColors dir="rtl" toastOptions={{ style: { fontFamily: "Cairo" } }} />
            </CartProvider>
          </MeProvider>
        </ClerkAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
